import React, { useMemo } from "react"

import * as PapaParse from "papaparse"

import { AmplitudeEvent } from "@common-shared/config/amplitude.config"
import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { AvailableLookupConsumer } from "@common-shared/config/lookup.config"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { IFileToUploadRequestData } from "@common-shared/types/requestData/commonRequestData.types"

import { ButtonElement } from "@front-shared/elements/button.element"
import { DropZoneFileElement } from "@front-shared/elements/dropZoneFile.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { useCptCodes } from "@front-shared/hooks/cptCodes.hook"

import {
    CHECKER_CONFIG,
    CoverageCheckerRunningState,
} from "../../../config/checker.config"
import { CSV_CHECKER_CONFIG } from "../../../config/csvChecker.config"
import { AmplitudeHelper } from "../../../helpers/amplitude.helper"
import { CsvHelper } from "../../../helpers/csv.helper"
import { ProduktlyHelper } from "../../../helpers/produktly.helper"
import { UtilHelper } from "../../../helpers/util.helper"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { checkerSelector } from "../../../store/selectors/checker.selector"
import { profileSelector } from "../../../store/selectors/profile.selector"
import { selectedPracticeConfigurationSelector } from "../../../store/selectors/selectedPracticeConfiguration.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import {
    checkerSetContinuousMonitoringInputData,
    checkerSetMedicaidInputData,
    checkerSetRunningState,
} from "../../../store/slices/checker.slice"
import type { IDynamicCheckerFormInputData } from "../../../types/data.types"

export const UploadCsvComponent: React.FunctionComponent<{
    className?: string
}> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const { csvCheckType } = useAppSelector(checkerSelector)
    const profile = useAppSelector(profileSelector)

    const selectedPracticeConfiguration = useAppSelector(
        selectedPracticeConfigurationSelector,
    )

    const earlyReleaseFeaturesEligible = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_EARLY_RELEASE_FEATURES,
    )

    const { availableCptCodesByModalities } = useCptCodes(
        AvailableLookupConsumer.coveragePortal,
        selectedPracticeRole?.availableModalities,
    )

    const templateUrl = useMemo<string | null>(() => {
        return CSV_CHECKER_CONFIG.earlyReleaseItems.fields.length &&
            earlyReleaseFeaturesEligible
            ? CSV_CHECKER_CONFIG.csvCheckerTemplateUrlEarlyRelease[csvCheckType]
            : CSV_CHECKER_CONFIG.csvCheckerTemplateUrl[csvCheckType]
    }, [csvCheckType, earlyReleaseFeaturesEligible])

    const supportedPayersUrl = useMemo<string | null>(() => {
        return CHECKER_CONFIG.supportedPayersUrl[csvCheckType]
    }, [csvCheckType])

    // https://www.papaparse.com/
    const processCsv = (providedCsvFile: IFileToUploadRequestData) => {
        if (!providedCsvFile?.rawFile) {
            return
        }

        dispatch(
            checkerSetRunningState(CoverageCheckerRunningState.PROCESS_CSV),
        )

        PapaParse.parse(providedCsvFile.rawFile, {
            header: true,
            skipEmptyLines: true,

            // Since in our csv we have some random values for header
            // We'd like to transform it to what code expects
            transformHeader(header: string, index: number): string {
                const headerKeys = CSV_CHECKER_CONFIG.uploadCsvHeaderKeys[
                    csvCheckType
                ].filter(
                    key =>
                        !CSV_CHECKER_CONFIG.earlyReleaseItems.fields.includes(
                            key,
                        ) || earlyReleaseFeaturesEligible,
                )

                return headerKeys[index] || header
            },

            complete: async csv => {
                let processedInputData: IDynamicCheckerFormInputData[] | null =
                    null

                const maxBulkCoverageChecksCSV = UtilHelper.isInternalUser(
                    profile,
                )
                    ? CSV_CHECKER_CONFIG.maxBulkCoverageChecksCSVExtended
                    : CSV_CHECKER_CONFIG.maxBulkCoverageChecksCSV

                let isMedicaidProcessedInputData = false

                switch (csvCheckType) {
                    case QuotaUsageType.GENERAL:
                    case QuotaUsageType.DISCOVER:
                    case QuotaUsageType.SMART_SCAN:
                    case QuotaUsageType.AGENTIC_PORTAL:
                    case QuotaUsageType.AGENTIC_PHONE:
                        processedInputData =
                            await CsvHelper.processUploadedContinuousMonitoringCsv(
                                csv,
                                availableCptCodesByModalities,
                                csvCheckType,
                                selectedPracticeRole,
                                maxBulkCoverageChecksCSV,
                                selectedPracticeConfiguration?.payerMappingsMode,
                            )

                        break

                    case QuotaUsageType.MEDICAID:
                        processedInputData =
                            await CsvHelper.processUploadedMedicaidCsv(
                                csv,
                                availableCptCodesByModalities,
                                csvCheckType,
                                selectedPracticeRole,
                                maxBulkCoverageChecksCSV,
                            )

                        isMedicaidProcessedInputData = true

                        break
                }

                if (processedInputData) {
                    dispatch(
                        isMedicaidProcessedInputData
                            ? checkerSetMedicaidInputData(processedInputData)
                            : checkerSetContinuousMonitoringInputData(
                                  processedInputData,
                              ),
                    )
                }

                // Verify that one or more rows were parsed successfully
                const partialSuccess = processedInputData?.some(
                    item => item.csvValidationErrors?.length === 0,
                )

                AmplitudeHelper.logEvent(AmplitudeEvent.UPLOADED_CSV, {
                    practiceId: selectedPracticeRole?.practice.id,
                    practiceName: selectedPracticeRole?.practice.name,
                    csvCheckType,
                    fileSizeKb: providedCsvFile?.rawFile?.size
                        ? providedCsvFile.rawFile.size / 1000
                        : 0,
                    checksCount: processedInputData?.length || 0,
                    success: partialSuccess,
                })

                if (processedInputData) {
                    // So breathing loader looks good
                    await UtilHelper.sleep(2)

                    dispatch(
                        checkerSetRunningState(
                            CoverageCheckerRunningState.PREVIEW_CSV,
                        ),
                    )
                }
            },
        })
    }

    return (
        <>
            <div
                className={`
                    ${props.className}
                    relative bg-brand-warmLight025 p-[16px]
                `}
            >
                <div className="flex gap-[24px] items-stretch">
                    <div className="flex-1">
                        <div
                            data-produktly={`checker-bulk-required-fields-${ProduktlyHelper.formatId(
                                csvCheckType,
                            )}`}
                        >
                            <PrimaryText
                                typography="text"
                                size={18}
                                lineHeight={27}
                                weight="bold"
                            >
                                Required Fields
                            </PrimaryText>

                            <div className="p-[16px] grid grid-flow-row auto-cols-fr gap-x-[24px] gap-y-[2px] grid-cols-2">
                                {CSV_CHECKER_CONFIG.availableCSVFieldsToDisplay.map(
                                    (field, index) => {
                                        const isRequired =
                                            CSV_CHECKER_CONFIG.requiredFields[
                                                csvCheckType
                                            ].includes(field)

                                        const columnConfig =
                                            CSV_CHECKER_CONFIG
                                                .uploadCsvPreviewMapping[field]

                                        return columnConfig ? (
                                            <PrimaryText
                                                key={index}
                                                className={
                                                    !isRequired
                                                        ? "opacity-20"
                                                        : ""
                                                }
                                                typography="text"
                                                weight="medium"
                                            >
                                                {columnConfig.header
                                                    .split("(")[0]
                                                    .trim()}
                                            </PrimaryText>
                                        ) : null
                                    },
                                )}
                            </div>
                        </div>

                        <div className="mt-[8px] flex items-center">
                            <div className="flex-1 flex items-center">
                                {supportedPayersUrl ? (
                                    <a
                                        data-produktly="checker-bulk-supported-payers"
                                        href={supportedPayersUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <ButtonElement
                                            label="See Supported Payers"
                                            type="default"
                                            htmlType="button"
                                        />
                                    </a>
                                ) : null}
                            </div>

                            {templateUrl ? (
                                <div>
                                    <a
                                        data-produktly="checker-bulk-download-csv"
                                        href={templateUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        download
                                    >
                                        <ButtonElement
                                            label="Download CSV"
                                            type="primary"
                                            htmlType="button"
                                        />
                                    </a>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <DropZoneFileElement
                        className="w-[290px] flex items-center justify-center"
                        withRawFile={true}
                        onFileAccepted={file => {
                            processCsv(file)
                        }}
                        acceptMimes={{
                            "text/csv": [],
                        }}
                    />
                </div>
            </div>
        </>
    )
}
